import config from '@/config.js'
import { getStatic } from '@/services/data-source.js'

const LEMMA_SEARCH_SCHEMA_VERSION = 1
const LEMMA_SEARCH_FIELDS = [
  'articleId',
  'articleLemma',
  'articlePos',
  'articleLanguageCode',
  'mainLemmaId',
  'mainLemma',
  'mainLemmaPos',
  'mainLemmaLanguageCode',
  'lemmaId',
  'lemma',
  'lemmaPos',
  'lemmaLanguageCode',
  'lemmaScriptae',
  'dictionary',
]
const FILTER_FIELDS = {
  articleLemma: 'articleLemma',
  articleLanguage: 'articleLanguageCode',
  mainLemma: 'mainLemma',
  mainLemmaLanguage: 'mainLemmaLanguageCode',
  lemma: 'lemma',
  lemmaLanguage: 'lemmaLanguageCode',
}
const DEFAULT_SORT_FIELD = 'lemma'
const DEFAULT_PAGE_SIZE = 100
const PAGE_SIZES = Object.freeze([25, 50, 100, 250])
const FILTER_CRITERIA_FIELDS = Object.freeze(['search', ...Object.keys(FILTER_FIELDS)])
const SORT_CACHE_INDEX = Symbol('lemmaSortCacheIndex')

let staticRowsPromise = null
let staticRowsBaseUrl = null

function dictionaryLanguageCodes(dictionaries, selectedDictionaryAcronyms) {
  const selected = new Set((selectedDictionaryAcronyms || []).map(String))
  if (!selected.size) return null

  return new Set(
    (dictionaries || [])
      .filter(dictionary => selected.has(String(dictionary.acronym)))
      .map(dictionary => dictionary.languageCode)
      .filter(Boolean)
      .map(String)
  )
}

function filterLemmaLanguageOptions(languages, dictionaries, selectedDictionaryAcronyms) {
  const allowed = dictionaryLanguageCodes(dictionaries, selectedDictionaryAcronyms)
  if (!allowed) return languages || []
  return (languages || []).filter(language => allowed.has(String(language.code)))
}

function unaccent(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function searchableValue(value) {
  return unaccent(String(value || '').replace(/<[^>]*>/g, ''))
}

function validateNormalizedLemmaRow(row, index) {
  if (row.lemmaId === null || row.lemmaId === undefined || row.lemma === null || row.lemma === undefined) {
    throw new Error(`Lemma search row ${index} is missing lemmaId or lemma`)
  }

  row.lemmaId = String(row.lemmaId)
  if (row.articleId !== null) row.articleId = String(row.articleId)
  if (row.mainLemmaId !== null) row.mainLemmaId = String(row.mainLemmaId)
  Object.defineProperty(row, SORT_CACHE_INDEX, { value: index })
  return Object.freeze(row)
}

function normalizeLemmaRow(rawRow, index) {
  let row = rawRow
  if (Array.isArray(row) && row.length === 1 && row[0] && typeof row[0] === 'object') {
    row = row[0]
  }
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    throw new Error(`Lemma search row ${index} must be an object`)
  }
  const normalized = {}
  LEMMA_SEARCH_FIELDS.forEach(field => {
    normalized[field] = row[field] === undefined ? null : row[field]
  })
  return validateNormalizedLemmaRow(normalized, index)
}

function normalizeCompactLemmaRow(values, index) {
  if (!Array.isArray(values) || values.length !== LEMMA_SEARCH_FIELDS.length) {
    throw new Error(`Lemma search row ${index} has an invalid field count`)
  }

  const normalized = {}
  for (let fieldIndex = 0; fieldIndex < LEMMA_SEARCH_FIELDS.length; fieldIndex++) {
    normalized[LEMMA_SEARCH_FIELDS[fieldIndex]] = values[fieldIndex]
  }
  return validateNormalizedLemmaRow(normalized, index)
}

function normalizeCompactPayload(payload) {
  if (payload.schemaVersion !== LEMMA_SEARCH_SCHEMA_VERSION) {
    throw new Error(`Unsupported lemma search schema version: ${payload.schemaVersion}`)
  }
  if (!Array.isArray(payload.fields) || !Array.isArray(payload.rows)) {
    throw new Error('Versioned lemma search data must contain fields and rows arrays')
  }
  if (
    payload.fields.length !== LEMMA_SEARCH_FIELDS.length ||
    payload.fields.some((field, index) => field !== LEMMA_SEARCH_FIELDS[index])
  ) {
    throw new Error('Lemma search fields do not match the expected schema')
  }

  return payload.rows.map(normalizeCompactLemmaRow)
}

function normalizeLemmaListPayload(payload) {
  const rows = Array.isArray(payload)
    ? payload.map(normalizeLemmaRow)
    : normalizeCompactPayload(payload)
  return Object.freeze(rows)
}

function compileFilter(value, label) {
  const expression = String(value || '').trim()
  if (!expression) return null
  try {
    return new RegExp(unaccent(expression), 'iu')
  } catch (error) {
    throw new Error(`Invalid ${label} regular expression: ${error.message}`)
  }
}

function articleIdsForLemmaIds(rows, lemmaIds) {
  const articleIds = new Set()
  rows.forEach(row => {
    if (row.articleId !== null && lemmaIds.has(String(row.lemmaId))) {
      articleIds.add(String(row.articleId))
    }
  })
  return articleIds
}

function filterLemmaRows(rows, criteria = {}, candidateArticleIds = null) {
  const filters = Object.entries(FILTER_FIELDS)
    .map(([criteriaField, rowField]) => ({
      rowField,
      expression: compileFilter(criteria[criteriaField], criteriaField),
    }))
    .filter(filter => filter.expression)
  const globalExpression = compileFilter(criteria.search, 'search')
  const dictionaries = new Set(
    (criteria.dictionaryAcronyms || []).map(value => String(value))
  )

  if (
    !filters.length &&
    !globalExpression &&
    !dictionaries.size &&
    candidateArticleIds === null
  ) {
    return rows
  }

  const filtered = rows.filter(row => {
    if (
      candidateArticleIds !== null &&
      !candidateArticleIds.has(String(row.articleId))
    ) {
      return false
    }
    if (
      dictionaries.size &&
      row.dictionary !== null &&
      !dictionaries.has(String(row.dictionary))
    ) {
      return false
    }
    if (
      globalExpression &&
      !['articleLemma', 'mainLemma', 'lemma'].some(
        field => globalExpression.test(searchableValue(row[field]))
      )
    ) {
      return false
    }
    return filters.every(
      filter => filter.expression.test(searchableValue(row[filter.rowField]))
    )
  })
  return Object.freeze(filtered)
}

function loadStaticLemmaRows() {
  const baseUrl = String(config.apiUrlClientSide || '')
  if (!staticRowsPromise || staticRowsBaseUrl !== baseUrl) {
    staticRowsBaseUrl = baseUrl
    // This service caches the normalized promise. Bypassing the generic response
    // cache prevents it from retaining a second, raw parsed copy of every row.
    staticRowsPromise = getStatic('lemma_search.json', { cache: false })
      .then(response => normalizeLemmaListPayload(response.data))
      .catch(error => {
        staticRowsPromise = null
        staticRowsBaseUrl = null
        throw error
      })
  }
  return staticRowsPromise
}

function lemmaSortDisplayValue(value) {
  return String(value).replace(/<[^>]*>/g, '')
}

function createLemmaSortKeyCache(projector = lemmaSortDisplayValue) {
  let activeField = null
  let indexedKeys = []
  let unindexedKeys = new WeakMap()

  return Object.freeze({
    get(row, field) {
      if (field !== activeField) {
        activeField = field
        indexedKeys = []
        unindexedKeys = new WeakMap()
      }
      const index = row[SORT_CACHE_INDEX]
      if (Number.isInteger(index)) {
        if (!Object.prototype.hasOwnProperty.call(indexedKeys, index)) {
          indexedKeys[index] = projector(row[field])
        }
        return indexedKeys[index]
      }
      if (!unindexedKeys.has(row)) {
        unindexedKeys.set(row, projector(row[field]))
      }
      return unindexedKeys.get(row)
    },
    clear() {
      activeField = null
      indexedKeys = []
      unindexedKeys = new WeakMap()
    },
  })
}

function compareLemmaRows(rowA, rowB, key, collator, sortKeyCache) {
  const valueA = rowA[key]
  const valueB = rowB[key]
  const emptyA = valueA === null || valueA === undefined || valueA === ''
  const emptyB = valueB === null || valueB === undefined || valueB === ''
  if (emptyA && emptyB) return collator.compare(rowA.lemmaId, rowB.lemmaId)
  if (emptyA) return 1
  if (emptyB) return -1
  const comparison = collator.compare(
    sortKeyCache.get(rowA, key),
    sortKeyCache.get(rowB, key)
  )
  return comparison || collator.compare(rowA.lemmaId, rowB.lemmaId)
}

function parseLemmaListRouteQuery(query, sortableFields) {
  const filters = {}
  FILTER_CRITERIA_FIELDS.forEach(field => {
    filters[field] = typeof query[field] === 'string' ? query[field] : ''
  })
  const page = Number.parseInt(query.page, 10)
  const perPage = Number.parseInt(query.perPage, 10)

  return {
    filters,
    currentPage: Number.isInteger(page) && page > 0 ? page : 1,
    perPage: PAGE_SIZES.includes(perPage) ? perPage : DEFAULT_PAGE_SIZE,
    sortBy: sortableFields.includes(query.sort) ? query.sort : DEFAULT_SORT_FIELD,
    sortDesc: query.desc === 'true',
  }
}

function serializeLemmaListRouteQuery(state) {
  const query = {}
  Object.entries(state.filters).forEach(([field, value]) => {
    const trimmed = String(value || '').trim()
    if (trimmed) query[field] = trimmed
  })
  if (state.currentPage > 1) query.page = String(state.currentPage)
  if (state.perPage !== DEFAULT_PAGE_SIZE) query.perPage = String(state.perPage)
  if (state.sortBy !== DEFAULT_SORT_FIELD) query.sort = state.sortBy
  if (state.sortDesc) query.desc = 'true'
  return query
}

async function queryStaticLemmaRows(criteria, matchingLemmaIds = null) {
  const rows = await loadStaticLemmaRows()
  const candidateArticleIds = matchingLemmaIds === null
    ? null
    : articleIdsForLemmaIds(rows, matchingLemmaIds)
  const data = filterLemmaRows(rows, criteria, candidateArticleIds)
  return { data, count: data.length, candidateArticleIds }
}

function clearStaticLemmaListCache() {
  staticRowsPromise = null
  staticRowsBaseUrl = null
}

export {
  LEMMA_SEARCH_FIELDS,
  articleIdsForLemmaIds,
  compareLemmaRows,
  clearStaticLemmaListCache,
  createLemmaSortKeyCache,
  dictionaryLanguageCodes,
  filterLemmaRows,
  filterLemmaLanguageOptions,
  loadStaticLemmaRows,
  normalizeLemmaListPayload,
  parseLemmaListRouteQuery,
  queryStaticLemmaRows,
  serializeLemmaListRouteQuery,
}
